import * as THREE from 'three';
import { getSharedStandardMaterial } from '../../../../../utils/threeResourcePool';
import { FAB_COLORS, FAB_BODY_SHADING, FAB_LED_INTENSITY } from '../../rendering/fabColors';
import {
  createFabClearWhiteDistantGlass,
  createFabClearWhiteSidePhysicalGlass,
  createFabLightBlueDistantGlass,
  createFabLightBluePhysicalGlass,
  createFabSmokedDistantGlass,
  createFabSmokedPhysicalGlass,
} from '../../rendering/fabGlassMaterials';

/** Cached material keys for the nine reference material families. */
export type EquipmentMaterialKey =
  | 'whitePainted'
  | 'graphite'
  | 'aluminum'
  | 'blueGlass'
  | 'darkGlass'
  | 'cyanLed'
  | 'greenLed'
  | 'rubber'
  | 'foupPolymer';

const glassPhysicalCache = new Map<string, THREE.MeshPhysicalMaterial>();

function applyGlassHints(mat: THREE.Material): void {
  mat.depthWrite = false;
  mat.transparent = true;
  mat.side = THREE.DoubleSide;
}

/** 1 — White painted metal body panels. */
export function getWhitePaintedMetal(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: '#E6EAEE',
    ...FAB_BODY_SHADING.white,
    opacity,
    transparent: opacity < 1,
  });
}

/** 2 — Dark graphite structural panels. */
export function getGraphiteMetal(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.graphite,
    ...FAB_BODY_SHADING.graphite,
    opacity,
    transparent: opacity < 1,
  });
}

/** 3 — Brushed aluminum trim. */
export function getBrushedAluminum(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.brushedMetal,
    ...FAB_BODY_SHADING.silver,
    opacity,
    transparent: opacity < 1,
  });
}

/** 4 — Blue-tinted industrial process glass (physical, with thickness). */
export function getBlueIndustrialGlass(
  opacity = 1,
  quality: 'high' | 'low' = 'high'
): THREE.Material {
  if (quality === 'low') {
    return createFabLightBlueDistantGlass(opacity);
  }
  const key = `blue-glass:${opacity.toFixed(2)}`;
  let mat = glassPhysicalCache.get(key);
  if (!mat) {
    mat = createFabLightBluePhysicalGlass(opacity, 'fab-equipment-blue');
    applyGlassHints(mat);
    glassPhysicalCache.set(key, mat);
  }
  return mat;
}

/** Bright white side enclosure glass — reference 360° cyan glow through panels. */
export function getBrightSideGlass(
  opacity = 1,
  quality: 'high' | 'low' = 'high'
): THREE.Material {
  if (quality === 'low') {
    return createFabClearWhiteDistantGlass(opacity);
  }
  const key = `bright-side-glass:${opacity.toFixed(2)}`;
  let mat = glassPhysicalCache.get(key);
  if (!mat) {
    mat = createFabClearWhiteSidePhysicalGlass(opacity, 'fab-equipment-bright-side');
    applyGlassHints(mat);
    glassPhysicalCache.set(key, mat);
  }
  return mat;
}

/** 5 — Dark smoked chamber glass. */
export function getDarkChamberGlass(opacity = 1, quality: 'high' | 'low' = 'high'): THREE.Material {
  if (quality === 'low') {
    return createFabSmokedDistantGlass(opacity);
  }
  const key = `dark-glass:${opacity.toFixed(2)}`;
  let mat = glassPhysicalCache.get(key);
  if (!mat) {
    mat = createFabSmokedPhysicalGlass(opacity, 'fab-equipment-dark');
    applyGlassHints(mat);
    glassPhysicalCache.set(key, mat);
  }
  return mat;
}

/** 6 — Cyan emissive LED strip. */
export function getCyanLedMaterial(intensityScale = 1, opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.cyan,
    emissive: FAB_COLORS.cyan,
    emissiveIntensity: FAB_LED_INTENSITY.cyanEdge * intensityScale,
    metalness: 0.04,
    roughness: 0.35,
    toneMapped: false,
    opacity,
    transparent: opacity < 1,
  });
}

/** 7 — Green status indicator LED. */
export function getGreenLedMaterial(intensityScale = 1, opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.green,
    emissive: FAB_COLORS.green,
    emissiveIntensity: 0.75 * intensityScale,
    toneMapped: false,
    opacity,
    transparent: opacity < 1,
  });
}

/** 8 — Black rubber / plastic seals. */
export function getRubberMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: '#141820',
    metalness: 0.02,
    roughness: 0.82,
    opacity,
    transparent: opacity < 1,
  });
}

/** 9 — FOUP dark polymer shell. */
export function getFoupPolymerMaterial(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_COLORS.foupBody,
    metalness: 0.22,
    roughness: 0.48,
    envMapIntensity: 0.55,
    opacity,
    transparent: opacity < 1,
  });
}

/** Fresnel-style rim — thin emissive edge on glass (no shader required). */
export function getGlassEdgeGlowMaterial(intensityScale = 0.55, opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: '#5AD4F5',
    emissive: '#28D7FF',
    emissiveIntensity: 0.45 * intensityScale,
    metalness: 0,
    roughness: 0.2,
    toneMapped: false,
    transparent: true,
    opacity: Math.min(0.75, opacity * 0.7),
    depthWrite: false,
  });
}
